import React, { useContext, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import "./LoginPage.scss";
import { signIn, NEW_PASSWORD_REQUIRED } from "../AuthApi";
import { AuthContext, type AuthUser } from "../AuthContext";
import { useForm, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { MFAType } from "../MFA/MFAUtils";
import PasswordInput from "../../Forms/Controls/PasswordInput/PasswordInput";
import { EMAIL_PATTERN_REGEX } from "../../Forms/FormValidations";
import { TextButton, TextInput } from "@SeemplicityDev/shared";
import { useAppStyling } from "../../WhiteLabel/useAppStyling";
import { capitalize } from "lodash-es";
import {
  buildSamlPath,
  clearManualLogoutMarker,
  clearSsoFailure,
  getLastSsoProvider,
  hasRecentSsoFailure,
  isSsoAutoRedirectDisabled,
  isSsoDisabledByQuery,
  isSsoEnforced,
} from "../ssoSession";

interface InfoProps {
  type: string;
  message?: string;
}

export const Info: React.FC<InfoProps> = ({ type, message }) => {
  return (
    <div
      data-testid={`login-${type}`}
      className={`${type}-message-tip ${type}`}
    >
      <i className={`seem-icon ${type === "error" ? "seem-icon-error" : ""}`} />
      <span className="error-message">{message}</span>
    </div>
  );
};

interface LoginFormValues {
  username: string;
  password: string;
}

const LoginPage: React.FC = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { updateAuthState, populateAuthentication } = useContext(AuthContext);
  const {
    handleSubmit,
    control,
    reset,
    setError,
    formState: { errors },
  } = useForm<LoginFormValues>({
    mode: "onBlur",
  });

  const { t } = useTranslation();
  const { appProps } = useAppStyling();

  let history = useHistory();
  const location = useLocation();

  // Read once per mount: these client-side hints are written by AuthProvider,
  // and a value that changed mid-render would flip the form in and out of
  // existence.
  const { ssoProvider, ssoEnforced, hadSsoFailure } = useMemo(
    () => ({
      ssoProvider: getLastSsoProvider(),
      ssoEnforced: isSsoEnforced(),
      hadSsoFailure: hasRecentSsoFailure(),
    }),
    []
  );
  // A recorded IdP failure reveals the form too: it is the way out of a broken
  // IdP for a user who does not know about ?sso=off. Previews/localhost always
  // show it: sessions there are pasted prod tokens and the IdP round trip can
  // never come back (see isSsoAutoRedirectDisabled).
  const showPasswordForm =
    isSsoAutoRedirectDisabled() ||
    !ssoProvider ||
    !ssoEnforced ||
    isSsoDisabledByQuery(location.search) ||
    hadSsoFailure;

  const login = (data: LoginFormValues) => {
    // The IdP-failure marker is deliberately NOT cleared here: a wrong password
    // would otherwise re-arm a broken IdP for the next visit. A successful
    // authentication clears it in populateAuthentication.
    const { username, password } = data;

    setIsSubmitting(true);

    signIn(username?.toLowerCase(), password)
      .then((cognitoUser) => {
        // signIn resolves an aws-amplify CognitoUser augmented at runtime with
        // challengeName; AuthUser models that augmented shape (see AuthContext).
        const user = cognitoUser as unknown as AuthUser;
        const needsPasswordChange =
          user.challengeName === NEW_PASSWORD_REQUIRED;
        const needsMFA = user.challengeName === MFAType.SOFTWARE_TOKEN_MFA;

        updateAuthState({
          user,
        });

        if (needsPasswordChange) {
          history.push("/changePassword");
        } else if (needsMFA) {
          history.push("/mfa");
        } else {
          populateAuthentication(user, null);
          history.push("/dashboard");
        }
        reset();
      })
      .catch((err) => {
        setIsSubmitting(false);
        if (
          err.code === "NotAuthorizedException" ||
          err.code === "UserNotFoundException"
        ) {
          setError("username", { message: t("Invalid Username/Password") });
        } else {
          setError("username", {
            message: t("An error occurred. Login failed"),
          });
        }
      });
  };

  // One element, two homes: below Sign In as the secondary action when the
  // password form is visible, and directly under the hint when SSO is enforced
  // and the form is hidden. type="button" is load-bearing — inside a <form> an
  // untyped <button> defaults to type="submit" and would submit the login form.
  const ssoButton = !!ssoProvider && (
    <TextButton
      data-testid={"login-sso"}
      type="button"
      label={t("Continue with SSO")}
      isFullWidth
      isSquare
      size={"xl"}
      buttonStyle={showPasswordForm ? "secondary" : "primary"}
      className="login-sso-btn"
      onClick={() => {
        const samlPath = buildSamlPath(ssoProvider, location.search);
        if (!samlPath) {
          return;
        }
        // An explicit request for SSO lifts both vetoes: the user is asking
        // for the IdP, so a past failure and a past manual logout are moot.
        clearSsoFailure();
        clearManualLogoutMarker();
        history.push(samlPath);
      }}
    />
  );

  return (
    <div className="login-page-body">
      <h2 className="login-page-title">{t("Welcome")}</h2>
      <h3 className="login-page-subtitle normal">
        {t(`Sign in to ${capitalize(appProps?.orgName)}`)}
      </h3>
      {!!hadSsoFailure && (
        <Info
          type={"error"}
          message={t(
            "SSO sign-in failed. Please try again or contact your administrator."
          )}
        />
      )}

      {!showPasswordForm && (
        <>
          <p className="login-page-sso-hint">
            {t("Your organization signs in with single sign-on.")}
          </p>
          {ssoButton}
        </>
      )}

      {showPasswordForm && (
        <form className="login-page-form" onSubmit={handleSubmit(login)}>
          <Controller
            // @ts-expect-error wrapperClassName is not a Controller prop; dead prop ignored at runtime, kept to preserve behavior
            wrapperClassName="login-username"
            name={"username"}
            render={({ field: { onChange, onBlur, value } }) => {
              return (
                <TextInput
                  placeholder={"Email"}
                  isLarge
                  type={"text"}
                  disabled={isSubmitting}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  hideError
                  data-testid={"login-username"}
                />
              );
            }}
            control={control}
            rules={{
              required: true,
              pattern: {
                value: EMAIL_PATTERN_REGEX,
                message: t("Invalid username"),
              },
            }}
            disabled={isSubmitting}
          />
          <Controller
            // @ts-expect-error wrapperClassName is not a Controller prop; dead prop ignored at runtime, kept to preserve behavior
            wrapperClassName="login-password"
            name={"password"}
            render={({ field: { onChange, onBlur, value } }) => {
              return (
                <PasswordInput
                  disabled={isSubmitting}
                  placeholder={"Password"}
                  autoComplete={"current-password"}
                  isLarge
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  dataTestId={"login-password"}
                />
              );
            }}
            control={control}
            rules={{
              required: true,
            }}
            disabled={isSubmitting}
          />

          {!!errors?.username?.message && (
            <Info type={"error"} message={errors?.username?.message} />
          )}

          <TextButton
            data-testid={"login-submit"}
            type="submit"
            label={!isSubmitting ? "Sign In" : ""}
            isFullWidth
            rightIcon={isSubmitting ? "seem-icon-spinner spinner" : ""}
            buttonStyle={"primary"}
            disabled={isSubmitting}
            className="login-btn"
            size={"xl"}
            isSquare
          />
          {ssoButton}
        </form>
      )}
    </div>
  );
};

export default LoginPage;
